// STREAM_TYPES is the single source of truth — StreamType is derived from it so the
// list and the union can't drift apart.
export const STREAM_TYPES = ["ev", "obv"] as const;
export type StreamType = typeof STREAM_TYPES[number];

export const isStreamType = (value: unknown): value is StreamType =>
    typeof value === "string" && (STREAM_TYPES as readonly string[]).includes(value);

export type FieldMapping = {
    map?: Record<string, string>;
    default?: string;
    provider_field?: string;
    destination_field: string;
};

export type StreamEntry = Record<string, FieldMapping>;
export type ProviderEntry = Partial<Record<StreamType, StreamEntry>>;
export type FieldMappings = Record<string, ProviderEntry>;

// route.configuration.data may carry keys beyond field_mappings (e.g. subject_type,
// ca_uuids) that the frontend doesn't know about. PATCH replaces `data` wholesale on
// the backend, so we keep the unknown keys around via an index signature and spread
// them through on every write. Anything we explicitly type sits on top.
export type FieldMappingsConfig = {
    field_mappings?: FieldMappings;
    [key: string]: unknown;
};

// Field-level validation errors surfaced from the backend's 400 response, keyed by the
// builder input they map to.
export type FieldErrors = {
    destination_field?: string;
    provider_field?: string;
    default?: string;
    map?: string;
};

// A single rule = a (stream type, field-mapping) pair scoped to one
// connection-destination. Used by the panel, the code-editor validator, and the form.
export type Rule = {
    streamType: StreamType;
    mapping: FieldMapping;
};

export function getRuleForDestination(
    config: FieldMappingsConfig | undefined,
    providerId: string,
    destinationId: string
): Rule | null {
    const providerEntry = config?.field_mappings?.[providerId];
    if (!providerEntry) return null;
    for (const streamType of STREAM_TYPES) {
        const mapping = providerEntry[streamType]?.[destinationId];
        if (mapping) return { streamType, mapping };
    }
    return null;
}

// Strip empty optional fields so the payload matches the backend's expected shape
// (see Confluence "Field Mappings"). For obv this means `{ default, destination_field }`
// only — no empty `map` or `provider_field`. Unknown keys are dropped, and non-string
// values (which can sneak in via Code mode) are coerced/dropped so the backend doesn't
// reject the payload.
export function sanitizeMapping(mapping: FieldMapping): FieldMapping {
    const clean: FieldMapping = {
        destination_field: typeof mapping.destination_field === "string" ? mapping.destination_field : "",
    };
    if (typeof mapping.default === "string" && mapping.default !== "") {
        clean.default = mapping.default;
    }
    if (typeof mapping.provider_field === "string" && mapping.provider_field !== "") {
        clean.provider_field = mapping.provider_field;
    }
    if (mapping.map && typeof mapping.map === "object" && !Array.isArray(mapping.map)) {
        const cleanMap: Record<string, string> = {};
        for (const [key, value] of Object.entries(mapping.map)) {
            if (typeof key === "string" && key !== "" && typeof value === "string") {
                cleanMap[key] = value;
            }
        }
        if (Object.keys(cleanMap).length > 0) clean.map = cleanMap;
    }
    return clean;
}

// Remove a destination from every stream type under a provider, cleaning up any
// stream-type buckets that become empty. Mutates the provided providerEntry.
function removeDestinationAcrossStreams(
    providerEntry: ProviderEntry,
    destinationId: string,
    skipStream?: StreamType
): void {
    for (const streamType of STREAM_TYPES) {
        if (streamType === skipStream) continue;
        const streamEntry = providerEntry[streamType];
        if (!streamEntry || !(destinationId in streamEntry)) continue;
        const cleaned: StreamEntry = { ...streamEntry };
        delete cleaned[destinationId];
        if (Object.keys(cleaned).length === 0) {
            delete providerEntry[streamType];
        } else {
            providerEntry[streamType] = cleaned;
        }
    }
}

export function buildConfigWithRule(
    config: FieldMappingsConfig | undefined,
    providerId: string,
    destinationId: string,
    streamType: StreamType,
    mapping: FieldMapping
): FieldMappingsConfig {
    const previous = config ?? {};
    const fieldMappings: FieldMappings = { ...(previous.field_mappings ?? {}) };
    const providerEntry: ProviderEntry = { ...(fieldMappings[providerId] ?? {}) };

    removeDestinationAcrossStreams(providerEntry, destinationId, streamType);

    providerEntry[streamType] = {
        ...(providerEntry[streamType] ?? {}),
        [destinationId]: sanitizeMapping(mapping),
    };
    fieldMappings[providerId] = providerEntry;
    // Spread `previous` first so unknown sibling keys (subject_type, ca_uuids, …) are
    // preserved across PATCHes that would otherwise wipe them out.
    return { ...previous, field_mappings: fieldMappings };
}

export function buildConfigWithoutRule(
    config: FieldMappingsConfig | undefined,
    providerId: string,
    destinationId: string
): FieldMappingsConfig {
    const previous = config ?? {};
    const fieldMappings: FieldMappings = { ...(previous.field_mappings ?? {}) };
    if (!fieldMappings[providerId]) {
        return { ...previous, field_mappings: fieldMappings };
    }
    const providerEntry: ProviderEntry = { ...fieldMappings[providerId] };

    removeDestinationAcrossStreams(providerEntry, destinationId);

    if (Object.keys(providerEntry).length === 0) {
        delete fieldMappings[providerId];
    } else {
        fieldMappings[providerId] = providerEntry;
    }
    return { ...previous, field_mappings: fieldMappings };
}

// Parses the backend's 400 response. The shape is
//   { configuration: { data: { "field_mappings.<p>.<a>.<d>.<field>": "msg" } } }
// We only surface errors for the rule that's currently being saved (other rules' errors
// belong to other UI sessions).
export function parseServerErrors(
    responseBody: unknown,
    providerId: string,
    destinationId: string,
    streamType: StreamType
): FieldErrors {
    const errors: FieldErrors = {};
    if (typeof responseBody !== "object" || responseBody === null) return errors;
    const configuration = (responseBody as Record<string, unknown>).configuration;
    if (typeof configuration !== "object" || configuration === null) return errors;
    const data = (configuration as Record<string, unknown>).data;
    if (typeof data !== "object" || data === null) return errors;

    const prefix = `field_mappings.${providerId}.${streamType}.${destinationId}.`;
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
        if (typeof value !== "string" || !key.startsWith(prefix)) continue;
        const field = key.slice(prefix.length);
        if (field === "destination_field" || field === "provider_field" || field === "default" || field === "map") {
            errors[field] = value;
        }
    }
    return errors;
}
